import { useCallback, useRef } from 'react';
import { DayPilot } from '@daypilot/daypilot-lite-react';
import { decoupledSave, decoupledDelete } from '../../services/api';
import { buildFormFields, computeWorkloadForDay, detectOverlap, formatDuration, isPublicHoliday } from './utils';

export function useSchedulerCrud({
  events,
  setLoading,
  showToast,
  refreshAssignments,
  prjOptions,
  ctOptions,
  resOptions,
  taskOptions,
  customTaskTypes,
  publicHolidays,
  resources,
  isAdmin,
  capacityMode,
  overlapMode,
  holidayMode,
  showFormModal,
  showConfirmModal,
}) {
  const callbacksRef = useRef({});

  const validateAction = useCallback((resourceId, start, end, workload = 100, excludeId = null) => {
    const evs = events;
    const warnings = [];

    const holiday = isPublicHoliday(publicHolidays, start.toString());
    if (holiday) {
      const msg = `Jour férié : ${holiday.name}`;
      if (holidayMode === 'hard' && !isAdmin) return { blocked: true, message: msg };
      warnings.push(msg);
    }

    const startDate = new DayPilot.Date(start);
    const endDate = new DayPilot.Date(end);
    let d = startDate;
    let maxTotal = 0;
    while (d.getTime() < endDate.getTime()) {
      const dow = d.getDayOfWeek();
      if (dow !== 0 && dow !== 6) {
        const dayStr = d.toString('yyyy-MM-dd');
        const eventsExcluded = excludeId ? evs.filter((e) => e.id !== excludeId) : evs;
        const existing = computeWorkloadForDay(eventsExcluded, resourceId, dayStr);
        const total = existing + workload;
        if (total > maxTotal) maxTotal = total;
      }
      d = d.addDays(1);
    }

    if (maxTotal > 100) {
      const msg = `Charge totale dépassée (${maxTotal}% > 100%)`;
      if (overlapMode === 'strict' && !isAdmin) return { blocked: true, message: msg };
      warnings.push(msg);
    }

    return { blocked: false, message: warnings.join(' | ') || null };
  }, [events, publicHolidays, resources, isAdmin, overlapMode, holidayMode]);

  const editEvent = useCallback(async (eventData) => {
    const origStart = new DayPilot.Date(eventData.start).toString('yyyy-MM-ddTHH:mm:ss');
    const origEnd = new DayPilot.Date(eventData.end).toString('yyyy-MM-ddTHH:mm:ss');

    const form = buildFormFields(prjOptions, ctOptions, resOptions, taskOptions, false);
    const modal = await showFormModal(form, {
      text: eventData.text || '',
      resource: String(eventData.resource || ''),
      start: eventData.start,
      end: eventData.end,
      projectId: eventData._meta?.projectId ? String(eventData._meta.projectId) : '',
      taskId: eventData._meta?.taskId ? String(eventData._meta.taskId) : '',
      customTaskType: eventData._meta?.customTaskType || '',
      backColor: eventData.barColor || eventData.backColor || '#6fa8dc',
      notes: eventData._meta?.notes || '',
      workload: eventData._meta?.workload ?? 100,
    });
    if (modal.canceled) return;

    const result = modal.result;
    const ct = result.customTaskType ? customTaskTypes.find((t) => String(t.id || t.value) === result.customTaskType) : null;
    const color = ct?.color || result.backColor || '#6fa8dc';
    const wl = Number.isFinite(parseInt(result.workload, 10)) ? parseInt(result.workload, 10) : 100;

    const safeStart = result.start ? new DayPilot.Date(result.start).toString('yyyy-MM-ddTHH:mm:ss') : origStart;
    const safeEnd = result.end ? new DayPilot.Date(result.end).toString('yyyy-MM-ddTHH:mm:ss') : origEnd;

    try {
      setLoading(true);
      await decoupledSave('node', 'assignment', {
        title: result.text,
        start: safeStart,
        end: safeEnd,
        resource: result.resource,
        color,
        projectId: result.projectId || undefined,
        taskId: result.taskId || undefined,
        customTaskType: result.customTaskType || undefined,
        notes: result.notes || undefined,
        workload: wl,
      }, eventData.id);
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur édition : ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [prjOptions, ctOptions, resOptions, taskOptions, customTaskTypes, showToast, setLoading, showFormModal, refreshAssignments]);

  const handleDelete = useCallback(async (ev) => {
    const confirm = await showConfirmModal('Supprimer cette affectation ?');
    if (confirm.canceled) return;

    try {
      setLoading(true);
      await decoupledDelete('node', 'assignment', ev.id);
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur suppression : ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, setLoading, showConfirmModal, refreshAssignments]);

  const duplicateEvent = useCallback(async (eventData) => {
    try {
      setLoading(true);
      const newStart = new DayPilot.Date(eventData.start).addDays(1);
      const newEnd = new DayPilot.Date(eventData.end).addDays(1);
      await decoupledSave('node', 'assignment', {
        title: (eventData.text || '') + ' (copie)',
        start: newStart.toString('yyyy-MM-ddTHH:mm:ss'),
        end: newEnd.toString('yyyy-MM-ddTHH:mm:ss'),
        resource: eventData.resource,
        color: eventData.barColor || eventData.backColor || '#6fa8dc',
        projectId: eventData._meta?.projectId || undefined,
        taskId: eventData._meta?.taskId || undefined,
        customTaskType: eventData._meta?.customTaskType || undefined,
        notes: eventData._meta?.notes || undefined,
        workload: (() => { const w = parseInt(eventData._meta?.workload, 10); return Number.isFinite(w) ? w : 100; })(),
      });
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur duplication : ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, setLoading, refreshAssignments]);

  const changeColor = useCallback(async (eventData, newColor) => {
    try {
      setLoading(true);
      await decoupledSave('node', 'assignment', { color: newColor }, eventData.id);
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur couleur : ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, setLoading, refreshAssignments]);

  callbacksRef.current = { editEvent, handleDelete, duplicateEvent, changeColor };

  return { editEvent, handleDelete, duplicateEvent, changeColor, validateAction, callbacksRef };
}
